import PropTypes from 'prop-types';
import { Flex, Box, Image } from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import Empty from 'constants/empty';
import { radii, space } from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import EN from 'components/RichProfile/MyAccount/RecentOrders/locales/RecentOrders_en_US';
import FR from 'components/RichProfile/MyAccount/RecentOrders/locales/RecentOrders_fr_CA';

const MAX_THUMBNAILS_TO_DISPLAY = 6;
const MAX_THUMBNAILS_BEFORE_TRUNCATE = 5;
const getText = localeUtils.getLocalisedDataFromResource(EN, FR);

const styles = {
    thumbnail: {
        objectFit: 'cover',
        borderRadius: radii[2]
    },
    moreButton: {
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: space[2],
        minWidth: '40px',
        minHeight: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'auto',
        position: 'relative',
        zIndex: 1
    }
};

const getProductName = sku => sku.displayText || sku.displayName || getText('productFallback');

const getAltText = (productName, hasMore, isLastItem, remainingCount) => {
    if (!hasMore || !isLastItem) {
        return productName;
    }

    const moreText = remainingCount === 1 ? getText('product') : getText('products');

    return `${productName}, ${getText('and')} ${remainingCount} ${getText('more')} ${moreText}`;
};

const getAriaLabel = skuCount => {
    const productText = skuCount === 1 ? getText('product') : getText('products');

    return `${getText('orderContains')} ${skuCount} ${productText}`;
};

const renderThumbnail = (sku, index, thumbnailSize, hasMore, remainingCount, isLastDisplayed) => {
    const productName = getProductName(sku);
    const altText = getAltText(productName, hasMore, isLastDisplayed, remainingCount);

    return (
        <Box
            key={sku.skuId || index}
            width={thumbnailSize}
            height={thumbnailSize}
            flexShrink={0}
            data-at={Sephora.debug.dataAt('thumbnail_image')}
        >
            <Image
                src={sku.skuImages?.image135 || sku.image}
                alt={altText}
                width={thumbnailSize}
                height={thumbnailSize}
                css={styles.thumbnail}
            />
        </Box>
    );
};

const ProductThumbnails = ({ skus = Empty.Array, isMobile, onViewAllClick }) => {
    if (!skus || skus.length === 0) {
        return null;
    }

    const thumbnailSize = isMobile ? 48 : 56;
    const hasMore = skus.length > MAX_THUMBNAILS_TO_DISPLAY;
    const displayedSkus = hasMore ? skus.slice(0, MAX_THUMBNAILS_BEFORE_TRUNCATE) : skus;
    const remainingCount = hasMore ? skus.length - MAX_THUMBNAILS_BEFORE_TRUNCATE : 0;

    return (
        <Flex
            gap={2}
            alignItems='center'
            data-at={Sephora.debug.dataAt('product_thumbnails')}
            aria-label={getAriaLabel(skus.length)}
        >
            {displayedSkus.map((sku, index) => {
                const isLastDisplayed = index === displayedSkus.length - 1;

                return renderThumbnail(sku, index, thumbnailSize, hasMore, remainingCount, isLastDisplayed);
            })}
            {hasMore && (
                <Box
                    is='button'
                    type='button'
                    fontSize='base'
                    lineHeight='18px'
                    fontWeight='normal'
                    color='textSecondary'
                    onClick={onViewAllClick}
                    aria-label={`${getText('viewAll')} ${skus.length} ${skus.length === 1 ? getText('product') : getText('products')}`}
                    css={styles.moreButton}
                    data-at={Sephora.debug.dataAt('more_items_indicator')}
                    children={`+${remainingCount}`}
                />
            )}
        </Flex>
    );
};

ProductThumbnails.propTypes = {
    skus: PropTypes.arrayOf(
        PropTypes.shape({
            skuId: PropTypes.string,
            displayName: PropTypes.string,
            displayText: PropTypes.string,
            image: PropTypes.string,
            skuImages: PropTypes.object
        })
    ),
    isMobile: PropTypes.bool,
    onViewAllClick: PropTypes.func
};

export default wrapFunctionalComponent(ProductThumbnails, 'ProductThumbnails');
